import { IconButton } from '@carbon/react';
import { Copy } from '@carbon/react/icons';
import ErrorToast from 'components/error-toast';
import { SuccessToast } from 'components/success-toast';
import { copyCoordinates, type CopyResult, useCopied } from './hooks';
import type { LngLat } from './coordinate-utils';

/**
 * Copy icon with its own "Copied" flash.
 *
 * @param {object} props - Component props.
 * @param {LngLat} props.lngLat - The spot to copy.
 * @param {string} props.align - Where the tooltip opens.
 * @return {JSX.Element} The copy button.
 */
export function PinCopyButton({
  lngLat,
  align = 'left',
}: {
  lngLat: LngLat;
  align?: 'left' | 'top';
}): JSX.Element {
  const { copied, result, copy, clearResult } = useCopied();
  return (
    <>
      <IconButton
        label={copied ? 'Copied' : 'Copy coordinates'}
        kind="ghost"
        size="sm"
        align={align}
        onClick={() => void copy(() => copyCoordinates(lngLat))}
      >
        <Copy />
      </IconButton>
      <CopyToast result={result} onClose={clearResult} />
    </>
  );
}

/**
 * The top-right toast after a copy: "Copied to clipboard", or that the browser
 * wouldn't allow it.
 *
 * @param {object} props - Component props.
 * @param {CopyResult | null} props.result - How the copy went; null shows nothing.
 * @param {Function} props.onClose - Called when the toast goes.
 * @return {JSX.Element | null} The toast.
 */
export function CopyToast({
  result,
  onClose,
}: {
  result: CopyResult | null;
  onClose: () => void;
}): JSX.Element | null {
  /**
   * Clears the result, and lets Carbon hide the toast.
   *
   * @return {boolean} Always true.
   */
  const close = () => {
    onClose();
    return true;
  };
  if (result === 'copied') {
    return (
      <SuccessToast
        message="Copied to clipboard"
        timeout={2500}
        onClose={close}
      />
    );
  }
  if (result === 'failed') {
    return (
      <ErrorToast
        message="Couldn't copy to clipboard"
        subtitle="Your browser didn't allow it."
        onClose={close}
      />
    );
  }
  return null;
}
